<section class="images-shell">
  @if (error) {
    <p class="error">{{ error }}</p>
  }

  @if (sites.length) {
    <section class="table-panel">
      <header class="table-panel-header">
        <div>
          <p class="eyebrow">Library</p>
          <h2>Site images</h2>
          <p class="table-note">Sort the registry to inspect uploads, usage counts, and delete availability.</p>
        </div>
        <div class="header-actions">
          <button type="button" class="accent" [disabled]="loading" (click)="openUploadDialog()">Add image</button>
        </div>
      </header>

      @if (loading) {
        <p class="empty-state">Loading images...</p>
      } @else if (images.length) {
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Preview</th>
                <th scope="col" [attr.aria-sort]="sortStateFor('originalFileName')">
                  <button type="button" class="sort-button" (click)="setSort('originalFileName')">
                    File name
                    <span class="sort-indicator">{{ sortIndicatorFor('originalFileName') }}</span>
                  </button>
                </th>
                <th scope="col">Type</th>
                <th scope="col">Default alt</th>
                <th scope="col">Default caption</th>
                <th scope="col" [attr.aria-sort]="sortStateFor('createdAt')">
                  <button type="button" class="sort-button" (click)="setSort('createdAt')">
                    Uploaded
                    <span class="sort-indicator">{{ sortIndicatorFor('createdAt') }}</span>
                  </button>
                </th>
                <th scope="col" [attr.aria-sort]="sortStateFor('updatedAt')">
                  <button type="button" class="sort-button" (click)="setSort('updatedAt')">
                    Updated
                    <span class="sort-indicator">{{ sortIndicatorFor('updatedAt') }}</span>
                  </button>
                </th>
                <th scope="col" [attr.aria-sort]="sortStateFor('landingPageUsageCount')">
                  <button type="button" class="sort-button" (click)="setSort('landingPageUsageCount')">
                    Landing pages
                    <span class="sort-indicator">{{ sortIndicatorFor('landingPageUsageCount') }}</span>
                  </button>
                </th>
                <th scope="col" [attr.aria-sort]="sortStateFor('galleryUsageCount')">
                  <button type="button" class="sort-button" (click)="setSort('galleryUsageCount')">
                    Galleries
                    <span class="sort-indicator">{{ sortIndicatorFor('galleryUsageCount') }}</span>
                  </button>
                </th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (image of sortedImages; track image.id) {
                <tr>
                  <td>
                    <img class="thumb" [src]="image.contentUrl" [alt]="image.defaultAlt || image.originalFileName" />
                  </td>
                  <td class="file-cell">
                    <div class="file-meta">
                      <strong class="cell-truncate" [title]="image.originalFileName">{{ image.originalFileName }}</strong>
                    </div>
                  </td>
                  <td class="type-cell">
                    <code class="cell-truncate" [title]="image.contentType">{{ image.contentType }}</code>
                  </td>
                  <td>
                    <span class="cell-truncate" [title]="image.defaultAlt || 'None'">{{ image.defaultAlt || 'None' }}</span>
                  </td>
                  <td>
                    <span class="cell-truncate" [title]="image.defaultCaption || 'None'">{{ image.defaultCaption || 'None' }}</span>
                  </td>
                  <td>{{ image.createdAt | date: 'short' }}</td>
                  <td>{{ image.updatedAt | date: 'short' }}</td>
                  <td><span class="usage-chip">{{ image.landingPageUsageCount }}</span></td>
                  <td><span class="usage-chip">{{ image.galleryUsageCount }}</span></td>
                  <td class="actions-cell">
                    <div class="row-actions">
                      <button
                        type="button"
                        class="ghost small icon-button"
                        aria-label="Edit metadata"
                        title="Edit metadata"
                        (click)="openMetadataDialog(image.id)">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            d="M4 20h4.75L19 9.75 14.25 5 4 15.25V20Zm2-2v-1.92l8.25-8.25 1.92 1.92L7.92 18H6ZM18.37 3.88l1.75 1.75a1.5 1.5 0 0 1 0 2.12l-.92.92-3.87-3.88.92-.91a1.5 1.5 0 0 1 2.12 0Z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="danger small icon-button"
                        [attr.aria-label]="deletingImageId === image.id ? 'Deleting image' : 'Delete image'"
                        [attr.title]="!image.canDelete ? 'Image is in use' : deletingImageId === image.id ? 'Deleting image' : 'Delete image'"
                        [disabled]="!image.canDelete || deletingImageId === image.id"
                        (click)="deleteImage(image.id)">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            d="M9 3h6l1 2h4v2H4V5h4l1-2Zm1 6h2v8h-2V9Zm4 0h2v8h-2V9ZM7 9h2v8H7V9Zm1 12a2 2 0 0 1-2-2V8h12v11a2 2 0 0 1-2 2H8Z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <p class="empty-state">No images uploaded for this site yet.</p>
      }
    </section>
  } @else {
    <section class="table-panel">
      <header class="table-panel-header">
        <div>
          <p class="eyebrow">Media</p>
          <h2>No sites available</h2>
        </div>
      </header>
      <p class="empty-state">Create a site first before managing reusable images.</p>
    </section>
  }
</section>

@if (isUploadDialogOpen) {
  <div class="dialog-backdrop" (click)="closeUploadDialog()">
    <section
      class="dialog-panel images-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-image-title"
      (click)="$event.stopPropagation()">
      <header class="dialog-header">
        <div>
          <p class="eyebrow">Upload</p>
          <h2 id="upload-image-title">Add image</h2>
          <p class="dialog-copy">Upload one reusable image for {{ selectedSiteName }}.</p>
        </div>
        <button type="button" class="ghost small" [disabled]="uploading" (click)="closeUploadDialog()">Close</button>
      </header>

      <label>
        <span>File</span>
        <input type="file" accept="image/jpeg,image/png" (change)="onFileSelected($event)" />
      </label>

      @if (uploadFile) {
        <p class="selected-file">{{ uploadFile.name }}</p>
      }

      <label>
        <span>Default alt</span>
        <input [(ngModel)]="uploadAlt" placeholder="Homepage hero" />
      </label>

      <label>
        <span>Default caption</span>
        <input [(ngModel)]="uploadCaption" placeholder="Shown wherever this image is reused by default" />
      </label>

      <div class="dialog-actions">
        <button type="button" class="ghost" [disabled]="uploading" (click)="closeUploadDialog()">Cancel</button>
        <button type="button" class="accent" [disabled]="uploading || !uploadFile" (click)="uploadImage()">
          {{ uploading ? 'Uploading...' : 'Upload image' }}
        </button>
      </div>
    </section>
  </div>
}

@if (isMetadataDialogOpen && editingImage) {
  <div class="dialog-backdrop" (click)="closeMetadataDialog()">
    <section
      class="dialog-panel images-dialog metadata-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-image-title"
      (click)="$event.stopPropagation()">
      <header class="dialog-header">
        <div>
          <p class="eyebrow">Metadata</p>
          <h2 id="edit-image-title">{{ editingImage.originalFileName }}</h2>
          <p class="dialog-copy">Update the default alt text and caption used when this image is reused.</p>
        </div>
        <button type="button" class="ghost small" [disabled]="saving" (click)="closeMetadataDialog()">Close</button>
      </header>

      <div class="metadata-layout">
        <div class="preview-panel">
          <img class="preview" [src]="editingImage.contentUrl" [alt]="editingImage.defaultAlt || editingImage.originalFileName" />
          <p class="meta-note">Uploaded {{ editingImage.createdAt | date: 'short' }}</p>
          <p class="meta-note">Updated {{ editingImage.updatedAt | date: 'short' }}</p>
        </div>

        <div class="metadata-form">
          <label>
            <span>Default alt</span>
            <input [(ngModel)]="metadataAlt" />
          </label>

          <label>
            <span>Default caption</span>
            <input [(ngModel)]="metadataCaption" />
          </label>
        </div>
      </div>

      <div class="dialog-actions">
        <button type="button" class="ghost" [disabled]="saving" (click)="closeMetadataDialog()">Cancel</button>
        <button type="button" class="accent" [disabled]="saving" (click)="saveMetadata()">
          {{ saving ? 'Saving...' : 'Save metadata' }}
        </button>
      </div>
    </section>
  </div>
}
